/**
 * The landing page's first screen, as Marketing's model has it (Grow
 * Landing Model, version 42; owner, 2026-10-14: the dark photo-led hero was
 * "dark and depressing" and its pale button looked like a hospital's). A
 * soft band from Grow's cream to its sage mist (#F5F1E8 to #E8EDE7) with
 * dark type: the words on the left, the picture on the right. The picture is
 * the owner's own garden, a rounded 4:5 photo in a column about 24rem wide
 * at most, with Grow's Today screen on her phone (a 5px charcoal frame,
 * 190px wide, 220px on a computer) over its bottom-left corner, so the phone
 * reads as the main thing. The button is solid forest green with cream type.
 * On a phone the words come first and the picture under them.
 *
 * For a founding visitor the sub line's price is drawn as the server says it, whole and in the page's language
 * (facts.founding.priceWords: "$39 for your first year", "$39 el primer año"; C101); the app builds none.
 *
 * The photo is public/landing/hero.jpg: broccoli in a raised bed, from the
 * owner's website (ggp-frontend public/images/swycg/broccoli.jpg, her own
 * camera), resized with its camera data stripped. Today is
 * public/landing/today.jpg (her garden, Oct 9). The band, the type and the
 * button use Grow's fixed palette (grow-cream, grow-muted, grow-charcoal,
 * grow-forest), not the theme's, so the band reads the same in a dark theme.
 *
 * The headline follows the ad that brought the visitor (?ad=dinner,
 * ?ad=frost); otherwise it is the plain one. A visitor who came by an open
 * founding link (?founding=CODE) gets the founding welcome in its place: the
 * places, the free days and the first year's price are the server's (the
 * landing read, from the owner's Grow settings), never this file's, and the
 * button claims the place by going on to sign-up (the kept code is claimed
 * there, as before).
 *
 * When every founding place is taken (facts.foundingFull, the server's count
 * against founding.PLACES; L7) a visitor on the founding link gets the full
 * state in place of the words and the button: "All 40 founding places are
 * taken.", the founding waiting list's form and a quiet "Or start a free
 * garden" (sections/FoundingFull). The picture stays. With places left the
 * hero is as before.
 *
 * A visitor from the owner's own Instagram or Facebook (?from=ig, ?from=fb,
 * or kept from an earlier visit; landingFacts arrivalOf) gets her words in
 * place of the headline and the line under it: "This is the app I built for
 * my own garden.", her round photo (public/landing/friday.jpg) beside "You've
 * seen my garden on Instagram (Facebook). Grow is how I keep it going: ...",
 * "Friday · @kitchengardensdaily" under it, and "See my garden on Grow" to
 * her garden page (facts.ownerSlug, the Grow setting landing.OWNER_SLUG). Their trial note says the follower
 * trial's days (facts.trialDays is trial.FOLLOWER_DAYS for them): the trial
 * the backend gives the account they sign up. A founding link wins over it.
 * English only, as the whole landing page is (owner, 2026-10-13).
 *
 * The remodelled page (landing.V2) says the new words (landing.v2.hero.*): a
 * direct headline "Your garden's day, planned before your coffee's ready."
 * (marketing's model v44, owner-approved 2026-10-15; the same line as the
 * link-preview picture; the ads', the follower's and the founding headlines
 * are unchanged), the line under it "From seed packet to plate, Grow has
 * every plant covered. Add your first plant, and Grow does the rest." (under
 * the ads' headlines too), the button "Start your free trial", the trial note (the
 * trial's days, the yearly price and the Free plan's plants, as the server
 * sends them) and a second note that it works on a phone, tablet and computer.
 *
 * Words (en/es): landing.hero.*, landing.hero.follower.*, landing.v2.hero.*; the full state's landing.hero.foundingFull.*.
 *
 * @module Hero
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { foundingDay } from '@/features/billing/lib/foundingDay';
import { isFollower, usdOf } from '../lib/landingFacts';
import { FoundingFull } from './FoundingFull';
import type { LandingSectionProps } from '../landingTypes';

/** The headline's look: the display face, large, balanced over its lines. */
const HEADLINE = 'max-w-3xl font-grow text-4xl font-extrabold leading-[1.08] tracking-tight [text-wrap:balance] md:text-6xl';

/** The line under the headline. */
const SUB = 'max-w-xl text-lg md:text-xl';


/** The small notes under the button, in Grow's fixed muted type. */
const NOTE = 'text-sm text-grow-muted-foreground';

/**
 * The hero.
 *
 * @param props.arrival - Which ad's line to open with, or the owner's link (her own words).
 * @param props.facts - The founding welcome, when there is one, the trial's days and price, Free's plants and the owner's address.
 * @param props.onStart - Starts a garden.
 * @param props.v2 - Whether to say the remodelled page's words.
 */
export const Hero: React.FC<LandingSectionProps> = ({ arrival, facts, onStart, v2 }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const founding = facts.founding;
  // Every founding place taken: the full state and the waiting list, in place of the words and the button (L7).
  const full = facts.foundingFull === true;
  // The owner's own words, for a visitor from her Instagram or Facebook; a founding link wins.
  const follower = !founding && !full && isFollower(arrival);
  // The founder price said whole by the server, in the page's language (C101).
  const price = founding?.priceWords ?? (founding ? usdOf(founding.price) : null);
  return (
    <section className="bg-gradient-to-b from-grow-cream to-grow-muted px-4 pb-12 pt-10 text-grow-charcoal md:px-8 md:pb-20 md:pt-16">
      <div className="mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-[1.05fr_0.95fr] md:gap-12">
        <div className="min-w-0 space-y-5 md:space-y-6">
          {full ? (
            <FoundingFull places={facts.foundingPlaces} arrival={arrival} onStart={onStart} />
          ) : (
            <>
              {founding ? (
                <>
                  <h1 className={HEADLINE}>{t('landing.hero.founding.title', { places: facts.foundingPlaces })}</h1>
                  <p className={SUB}>
                    {t('landing.hero.founding.sub', { date: foundingDay(founding.freeUntil, language), price: price ?? '' })}
                  </p>
                </>
              ) : follower ? (
                <>
                  <h1 className={HEADLINE}>{t('landing.hero.follower.headline')}</h1>
                  <div className="flex items-center gap-3">
                    <img
                      src="/landing/friday.jpg"
                      alt={t('landing.hero.follower.photoAlt')}
                      width={56}
                      height={56}
                      className="h-14 w-14 shrink-0 rounded-full border-[3px] border-white object-cover shadow-[0_2px_8px_rgb(0_0_0/0.15)]"
                    />
                    <p className="text-lg leading-snug md:text-xl">
                      {arrival === 'fb' ? t('landing.hero.follower.intro.fb') : t('landing.hero.follower.intro.ig')}
                      <br />
                      <span className="text-base text-grow-muted-foreground">{t('landing.hero.follower.byline', { handle: facts.ownerSlug })}</span>
                    </p>
                  </div>
                  <Link
                    to={`/@${facts.ownerSlug}`}
                    className="inline-flex items-center gap-1 font-semibold text-grow-forest underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grow-forest"
                  >
                    {t('landing.hero.follower.gardenLink')}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </>
              ) : (
                <>
                  <h1 className={HEADLINE}>
                    {arrival === 'dinner' ? t('landing.hero.headline.dinner') : arrival === 'frost' ? t('landing.hero.headline.frost') : t(v2 ? 'landing.v2.hero.headline' : 'landing.hero.headline.direct')}
                  </h1>
                  <p className={SUB}>{t(v2 ? 'landing.v2.hero.sub' : 'landing.hero.sub')}</p>
                </>
              )}
              <div className="space-y-2">
                {/* Solid forest green with cream type, on the light band in every theme. */}
                <Button
                  size="lg"
                  className="h-12 bg-grow-forest px-8 text-base font-bold text-grow-cream shadow-lg hover:bg-grow-forest/90"
                  onClick={onStart}
                >
                  {founding ? t('landing.hero.founding.cta') : t(v2 ? 'landing.v2.hero.cta' : 'landing.hero.cta')}
                </Button>
                <p className={NOTE}>
                  {founding
                    ? t(v2 ? 'landing.v2.hero.foundingNote' : 'landing.hero.founding.note')
                    : v2
                      ? t('landing.v2.hero.note', { days: facts.trialDays, price: usdOf(facts.prices.year), plants: facts.freePlants })
                      : t('landing.hero.note', { plants: facts.freePlants })}
                </p>
                {v2 && <p className={NOTE}>{t('landing.v2.hero.devices')}</p>}
              </div>
            </>
          )}
        </div>
        {/* The owner's garden, with Today on her own phone over its bottom-left corner: the app as it is, not a drawing. */}
        <div className="relative mx-auto w-full max-w-[24rem] pb-6 pl-24 md:pb-8 md:pl-28">
          <img
            src="/landing/hero.jpg"
            alt={t('landing.hero.photoAlt')}
            width={1067}
            height={1600}
            className="block aspect-[4/5] h-auto w-full rounded-[2rem] object-cover object-[center_40%] shadow-2xl"
          />
          <img
            src="/landing/today.jpg"
            alt={t('landing.hero.todayAlt')}
            width={640}
            height={1044}
            className="absolute bottom-0 left-0 h-auto w-[190px] rounded-[1.8rem] border-[5px] border-grow-charcoal shadow-2xl md:w-[220px]"
          />
        </div>
      </div>
    </section>
  );
};
